import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Box, Grid, Card, CardContent, Typography, Button, CircularProgress,
  Alert, TextField, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox, FormControlLabel, Divider } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FilterListIcon from "@mui/icons-material/FilterList";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import LockOpenIcon from "@mui/icons-material/LockOpen";

const token = () => localStorage.getItem("biyz_token");
const authHead = () => ({ Authorization: `Bearer ${token()}`, "Content-Type": "application/json" });

export default function PaymentPlans() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const botFilter = (params.get("bot") || "").trim();      // ?bot=biGERMAN
  const upgradeMode = params.get("upgrade") === "1";        // ?upgrade=1

  const [packages, setPackages] = useState([]);
  const [currentPrice, setCurrentPrice] = useState(null);  // null = henüz yüklenmedi
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [couponMsg, setCouponMsg] = useState(null);
  const [discounts, setDiscounts] = useState({}); // { [pkgId]: discountedPrice } — kod uygulanınca fiyat gorsel guncellensin diye
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // ── Elit Üyelik (abonelik) akışı ─────────────────────────────
  const [subPkg, setSubPkg] = useState(null);              // seçilen paket → dialog açık
  const [card, setCard] = useState({ owner: "", number: "", exp: "", cvv: "" });
  const [consent, setConsent] = useState(true);
  const [subBusy, setSubBusy] = useState(false);
  const [subErr, setSubErr] = useState("");

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("status") === "success") setSuccess("Ödeme başarılı! Aboneliğiniz aktive edildi.");
    if (p.get("status") === "failed") setError("Ödeme başarısız. Tekrar deneyin.");
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const pkgRes = await fetch("/api/packages", { headers: authHead() });
      const pkgData = await pkgRes.json();
      const list = Array.isArray(pkgData) ? pkgData
        : (Array.isArray(pkgData?.data) ? pkgData.data : []);
      setPackages(list.filter(p => p.isActive !== false));

      if (upgradeMode) {
        try {
          const subRes = await fetch("/api/subscriptions/me", { headers: authHead() });
          const subData = await subRes.json();
          const activeSubs = Array.isArray(subData?.subscriptions)
            ? subData.subscriptions.filter(s => s?.isActive)
            : [];
          if (activeSubs.length > 0 && list.length > 0) {
            const activePackageIds = new Set(activeSubs.map(s => String(s.packageId)));
            const ownedPrices = list
              .filter(p => activePackageIds.has(String(p._id)))
              .map(p => Number(p.price) || 0);
            setCurrentPrice(ownedPrices.length > 0 ? Math.max(...ownedPrices) : 0);
          } else {
            setCurrentPrice(0);
          }
        } catch {
          setCurrentPrice(0);
        }
      }
    } catch {
      setError("Paketler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  // Ücretsiz paket (0₺ freemium) → doğrudan aktive
  const handleFree = async (pkg) => {
    setPaying(pkg._id); setError("");
    try {
      const res = await fetch("/api/payments/esnekpos/init", {
        method: "POST", headers: authHead(),
        body: JSON.stringify({ packageId: pkg._id, campaignCode: coupon || undefined }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || data.detail?.errorMessage || "İşlem başlatılamadı"); return; }
      if (data.free) { setSuccess("Plan başarıyla aktive edildi!"); return; }
      if (data.paymentPageUrl) { window.location.href = data.paymentPageUrl; return; }
      // Defansif parite (prod handleBuy'dan): init hosted form dönerse aç
      if (data.checkoutFormContent) {
        const w = window.open("", "_blank");
        w.document.write(data.checkoutFormContent);
        w.document.close();
      }
    } catch { setError("Sunucuya bağlanılamadı"); }
    finally { setPaying(null); }
  };

  // Ücretli paket → Elit Üyelik (abonelik) dialogunu aç
  const openSub = (pkg) => {
    setSubErr("");
    setCard({ owner: "", number: "", exp: "", cvv: "" });
    setConsent(true);
    setSubPkg(pkg);
  };

  // Aboneliği başlat → recurring/start (trialDays:0 = denemesiz, direkt aylık abonelik)
  const submitSub = async () => {
    if (!subPkg) return;
    setSubErr("");
    const digits = card.number.replace(/\D/g, "");
    const ex = card.exp.replace(/\D/g, "");           // MMYY
    const mm = ex.slice(0, 2);
    const yy = ex.slice(2);
    if (!card.owner.trim() || digits.length < 15 || mm.length < 2 || yy.length < 2 || card.cvv.length < 3) {
      setSubErr("Lütfen kart bilgilerini eksiksiz girin."); return;
    }
    if (!consent) { setSubErr("Devam etmek için onay kutusunu işaretleyin."); return; }
    setSubBusy(true);
    try {
      const res = await fetch("/api/payments/esnekpos/recurring/start", {
        method: "POST", headers: authHead(),
        body: JSON.stringify({
          packageId: subPkg._id,
          trialDays: 0,
          campaignCode: coupon || undefined,
          card: {
            CC_OWNER: card.owner.trim(),
            CC_NUMBER: digits,
            EXP_MONTH: mm,
            EXP_YEAR: yy.length === 2 ? ("20" + yy) : yy,
            CC_CVV: card.cvv,
          },
        }),
      });
      const d = await res.json();
      if (!res.ok || !d.ok) {
        setSubErr(d.error === "recurring_init_failed"
          ? (d.detail?.message || "Ödeme reddedildi, kartını kontrol et")
          : (d.error || "Abonelik başlatılamadı"));
        return;
      }
      setSubPkg(null);
      setSuccess("👑 Elit üyeliğin başladı! İstediğin an tek tıkla iptal edebilirsin.");
    } catch { setSubErr("Sunucuya bağlanılamadı"); }
    finally { setSubBusy(false); }
  };

  const handleCoupon = async (pkg, codeOverride) => {
    const codeToUse = codeOverride || coupon;
    if (!codeToUse) return;
    try {
      const res = await fetch("/api/payments/campaign/validate", {
        method: "POST", headers: authHead(),
        body: JSON.stringify({ packageId: pkg._id, code: codeToUse }),
      });
      const d = await res.json();
      setCouponMsg(d.success
        ? { ok: true, msg: `✅ İndirim: ${d.discountedPrice} TL` }
        : { ok: false, msg: d.message || "Geçersiz kod" });
      if (d.success) {
        setDiscounts(prev => ({ ...prev, [pkg._id]: d.discountedPrice }));
      }
    } catch { setCouponMsg({ ok: false, msg: "Kontrol edilemedi" }); }
  };

  // Kampanya linkinden (?code=biuzman) gelindiyse: kodu alana yaz + ilgili paket(ler)e otomatik uygula
  const [autoApplied, setAutoApplied] = useState(false);
  useEffect(() => {
    const codeParam = params.get("code");
    if (!autoApplied && codeParam && packages.length > 0) {
      setCoupon(codeParam);
      const targets = packages.filter(p => Number(p.price) > 0 &&
        (!botFilter || p.name.toLowerCase().includes(botFilter.toLowerCase())));
      targets.forEach(p => handleCoupon(p, codeParam));
      setAutoApplied(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packages]);

  const visiblePackages = packages.filter(pkg => {
    const price = Number(pkg.price) || 0;
    if (botFilter) {
      const nameOk = pkg.name.toLowerCase().includes(botFilter.toLowerCase());
      if (!nameOk) return false;
    }
    if (upgradeMode && currentPrice !== null) {
      if (price <= currentPrice) return false;
      if (price === 0) return false;
    }
    return true;
  });

  if (loading) return <Box sx={{ display:"flex", justifyContent:"center", mt:8 }}><CircularProgress /></Box>;
  if (upgradeMode && currentPrice === null) {
    return <Box sx={{ display:"flex", justifyContent:"center", mt:8 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: "auto" }}>
      {botFilter ? (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
            <FilterListIcon sx={{ color: "#1565c0" }} />
            <Typography variant="h5" fontWeight={700}>{botFilter} Planları</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {botFilter} yapay zeka eğitmenine erişmek için aşağıdaki planlardan birini seçin.
          </Typography>
        </Box>
      ) : upgradeMode ? (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
            <UpgradeIcon sx={{ color: "#1565c0" }} />
            <Typography variant="h5" fontWeight={700}>Plan Yükselt</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Mevcut planınızdan daha kapsamlı paketler aşağıda listelenmiştir.
          </Typography>
          {currentPrice !== null && currentPrice > 0 && (
            <Chip label={`Mevcut plan: ${currentPrice} TL`} size="small"
              sx={{ mt: 1, bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 600 }} />
          )}
        </Box>
      ) : (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={700} mb={0.5}>Elit Üyelik Paketleri</Typography>
          <Typography variant="body2" color="text.secondary">
            Bir eğitim seç, Elit Üye ol — kesintisiz öğren, istediğin an tek tıkla iptal et.
          </Typography>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {!upgradeMode && (
        <Box sx={{ mb: 3, display: "flex", gap: 1, maxWidth: 360, alignItems: "center" }}>
          <TextField size="small" label="Kampanya Kodu" value={coupon}
            onChange={(e) => { setCoupon(e.target.value); setCouponMsg(null); }} fullWidth />
        </Box>
      )}
      {couponMsg && <Alert severity={couponMsg.ok ? "success" : "error"} sx={{ mb: 2 }}>{couponMsg.msg}</Alert>}

      {visiblePackages.length === 0 && !loading && (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography color="text.secondary" sx={{ mb: 1 }}>
            {upgradeMode ? "Mevcut planınızdan daha üst bir paket bulunmuyor." : "Henüz aktif paket bulunmuyor."}
          </Typography>
          {upgradeMode && (
            <Typography variant="body2" color="text.secondary">En kapsamlı planındasınız.</Typography>
          )}
        </Box>
      )}

      <Grid container spacing={2}>
        {visiblePackages.map(pkg => {
          const paid = Number(pkg.price) > 0;
          const discPrice = discounts[pkg._id];
          const hasDisc = paid && discPrice != null && Number(discPrice) < Number(pkg.price);
          return (
          <Grid item xs={12} sm={6} md={4} key={pkg._id}>
            <Card sx={{
              height: "100%", display: "flex", flexDirection: "column",
              border: "2px solid", borderColor: "#e0e0e0",
              "&:hover": { borderColor: "#1565c0", boxShadow: 4 },
              transition: "all 0.2s",
            }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ fontSize: 15 }}>
                  {pkg.name}
                </Typography>
                <Typography variant="h4" color="primary" fontWeight={700} sx={{ my: 1, fontSize: 28 }}>
                  {paid ? (
                    hasDisc ? (
                      <>
                        <Typography component="span" sx={{ textDecoration: "line-through", color: "text.secondary", fontSize: 16, mr: 1, fontWeight: 500 }}>{pkg.price} TL</Typography>
                        {discPrice} TL
                      </>
                    ) : `${pkg.price} TL`
                  ) : "Ücretsiz"}
                  {paid && <Typography component="span" variant="body2" color="text.secondary"> /ay</Typography>}
                </Typography>
                {pkg.duration && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {paid ? "aylık otomatik yenilenir" : `${pkg.duration} gün`}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: 13 }}>
                  {pkg.description}
                </Typography>
                {paid && (
                  <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                    <LockOpenIcon fontSize="small" sx={{ color: "#16a34a" }} />
                    <Typography variant="body2" sx={{ fontSize: 12.5, color: "#16a34a", fontWeight: 600 }}>
                      İstediğin an tek tıkla iptal
                    </Typography>
                  </Box>
                )}
              </CardContent>
              <Box sx={{ p: 2, pt: 0, display: "flex", gap: 1, flexDirection: "column" }}>
                {!upgradeMode && coupon && paid && (
                  <Button size="small" variant="outlined" onClick={() => handleCoupon(pkg)}>Kodu Uygula</Button>
                )}
                <Button fullWidth variant="contained" size="large"
                  startIcon={paid ? <WorkspacePremiumIcon /> : null}
                  disabled={paying === pkg._id}
                  onClick={() => paid ? openSub(pkg) : handleFree(pkg)}
                  sx={{ background: "linear-gradient(135deg,#1a6fc4,#2196f3)", fontWeight: 700 }}>
                  {paying === pkg._id
                    ? <CircularProgress size={20} color="inherit" />
                    : paid ? "Elit Üye Ol" : "Ücretsiz Başlat"}
                </Button>
              </Box>
            </Card>
          </Grid>
          );
        })}
      </Grid>

      {/* ══════════ ELİT ÜYELİK (ABONELİK) DIALOG ══════════ */}
      <Dialog open={!!subPkg} onClose={() => !subBusy && setSubPkg(null)} maxWidth="sm" fullWidth>
        {subPkg && (
          <>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 800 }}>
              <WorkspacePremiumIcon sx={{ color: "#c9a227" }} /> {subPkg.name} — Elit Üyelik
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ bgcolor: "#e9fbf1", border: "1px solid #b7ebcf", borderRadius: 2, p: 1.5, mb: 2,
                display: "flex", alignItems: "center", gap: 1 }}>
                <LockOpenIcon sx={{ color: "#0f7a43" }} />
                <Typography sx={{ color: "#0f7a43", fontWeight: 700, fontSize: 14 }}>
                  İstediğin an tek tıkla iptal — taahhüt yok, ceza yok.
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                {["♾️ Sınırsız yazılı sohbet + sesli konuşma pratiği",
                  "🔄 Kesintisiz devam — her ay yeniden satın alma derdi yok",
                  "🧠 İlerlemen ve geçmişin hep kayıtlı",
                  "🆕 Yeni özellikler üyeliğine bedava gelir"].map((t, i) => (
                  <Typography key={i} variant="body2" sx={{ fontSize: 13.5, mb: 0.5 }}>{t}</Typography>
                ))}
              </Box>

              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 2 }}>
                <Typography sx={{ fontWeight: 700 }}>Aylık üyelik</Typography>
                <Typography sx={{ fontWeight: 800, fontSize: 20, color: "#1565c0" }}>
                  {subPkg.price} ₺<Typography component="span" variant="body2" color="text.secondary"> /ay</Typography>
                </Typography>
              </Box>

              {subErr && <Alert severity="error" sx={{ mb: 2 }}>{subErr}</Alert>}

              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Kart Üzerindeki İsim" value={card.owner}
                    onChange={(e) => setCard({ ...card, owner: e.target.value })} placeholder="Ad Soyad" />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Kart Numarası" value={card.number}
                    onChange={(e) => setCard({ ...card, number: e.target.value })} placeholder="0000 0000 0000 0000" inputProps={{ inputMode: "numeric" }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="Son Kullanma (AA/YY)" value={card.exp}
                    onChange={(e) => setCard({ ...card, exp: e.target.value })} placeholder="AA/YY" />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="CVV" value={card.cvv}
                    onChange={(e) => setCard({ ...card, cvv: e.target.value })} placeholder="123" inputProps={{ inputMode: "numeric" }} />
                </Grid>
              </Grid>

              <FormControlLabel sx={{ mt: 1.5, alignItems: "flex-start" }}
                control={<Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} size="small" sx={{ pt: 0.3 }} />}
                label={<Typography variant="body2" sx={{ fontSize: 12.5, color: "#48505f" }}>
                  Bu dijital hizmetin hemen başladığını, aylık <b>{subPkg.price} ₺</b> otomatik yenileneceğini ve
                  <b> istediğim an iptal</b> edebileceğimi okudum, onaylıyorum.
                </Typography>} />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setSubPkg(null)} disabled={subBusy} color="inherit">Vazgeç</Button>
              <Button onClick={submitSub} disabled={subBusy} variant="contained"
                startIcon={!subBusy ? <WorkspacePremiumIcon /> : null}
                sx={{ background: "linear-gradient(135deg,#1a6fc4,#2196f3)", fontWeight: 700 }}>
                {subBusy ? <CircularProgress size={20} color="inherit" /> : "Elit Üyeliğimi Başlat"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
